// ============================================
// COMPONENT: MaintenanceModal
// ============================================
// Archivo: src/components/modals/MaintenanceModal.jsx

import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../common';

const MaintenanceModal = ({ 
  show, 
  maintenance = null,
  equipment = null,
  onClose, 
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    date: '',
    type: 'preventivo',
    description: '',
    technician: '',
    cost: '',
    duration: '',
    status: 'completado'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isEdit = !!maintenance;

  // Cargar datos si es edición
  useEffect(() => {
    if (maintenance) {
      setFormData({
        date: maintenance.date || '',
        type: maintenance.type || 'preventivo',
        description: maintenance.description || '',
        technician: maintenance.technician || '',
        cost: maintenance.cost || '',
        duration: maintenance.duration || '',
        status: maintenance.status || 'completado'
      });
    } else {
      // Reset en creación
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        date: today,
        type: 'preventivo',
        description: '',
        technician: '',
        cost: '',
        duration: '',
        status: 'completado'
      });
    }
  }, [maintenance, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.date) {
      newErrors.date = 'La fecha es obligatoria';
    }

    if (!formData.description || !formData.description.trim()) {
      newErrors.description = 'La descripción es obligatoria';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        equipmentId: equipment?.id,
        cost: parseFloat(formData.cost) || 0,
        duration: parseInt(formData.duration) || 0
      };

      await onSubmit(submitData, maintenance?.id);
      onClose();
    } catch (error) {
      console.error('Error al guardar mantenimiento:', error);
      setErrors({ general: error.message || 'Error al guardar mantenimiento' });
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { value: 'preventivo', label: 'Preventivo' },
    { value: 'correctivo', label: 'Correctivo' },
    { value: 'predictivo', label: 'Predictivo' }
  ];

  const statusOptions = [
    { value: 'completado', label: 'Completado' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'en_proceso', label: 'En Proceso' }
  ];

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={isEdit ? 'Editar Mantenimiento' : 'Registrar Mantenimiento'}
      maxWidth="2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Información del equipo */}
        {equipment && (
          <div className="bg-slate-700 p-4 rounded-lg mb-4">
            <p className="text-sm text-slate-300">
              <span className="font-semibold">Equipo:</span> {equipment.name}
            </p>
            <p className="text-sm text-slate-300">
              <span className="font-semibold">Código:</span> {equipment.code || 'Sin código'}
            </p>
          </div>
        )}

        {/* Error general */}
        {errors.general && (
          <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-2 rounded">
            {errors.general}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {/* Fecha */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Fecha de Mantenimiento <span className="text-red-400">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-400">{errors.date}</p>
            )}
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Tipo de Mantenimiento <span className="text-red-400">*</span>
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {typeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Descripción <span className="text-red-400">*</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Describe el mantenimiento realizado..."
            required
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-400">{errors.description}</p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Técnico */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Técnico Responsable
            </label>
            <input
              type="text"
              name="technician"
              value={formData.technician}
              onChange={handleChange}
              placeholder="Ej: Juan Pérez"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Estado */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Estado <span className="text-red-400">*</span>
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Costo */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Costo (CLP)
            </label>
            <input
              type="number"
              name="cost"
              value={formData.cost}
              onChange={handleChange}
              placeholder="0"
              min="0"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Duración */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Duración (horas)
            </label>
            <input
              type="number"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              placeholder="0"
              min="0"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Registrar')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default MaintenanceModal;
